"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useId, useState } from "react";
import * as RadixPopover from "@radix-ui/react-popover";
import { useAddressAutocomplete, type AddressSelection } from "@/hooks/use-address-autocomplete";
import { Spinner } from "@/components/spinner";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";

type AddressSearchInputProps = {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  onAddressSelect: (selection: AddressSelection) => void;
};

export const AddressSearchInput: FC<AddressSearchInputProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please input",
  id,
  value,
  onValueChange,
  onAddressSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const { suggestions, isLoading, search, select } = useAddressAutocomplete();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const isOpen = open && suggestions.length > 0;

  const handleChange = (next: string) => {
    onValueChange(next);
    search(next);
    setOpen(true);
    setHighlightedIndex(0);
  };

  const handleSelect = async (suggestion: (typeof suggestions)[number]) => {
    setOpen(false);
    const selection = await select(suggestion);
    if (!selection) return;
    onValueChange(selection.streetAddress);
    onAddressSelect(selection);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const suggestion = suggestions[highlightedIndex];
        if (suggestion) {
          event.preventDefault();
          void handleSelect(suggestion);
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex items-center gap-8">
        <FieldLabel htmlFor={inputId} state={state} required={required}>
          {label}
        </FieldLabel>
        {isLoading && <Spinner size={14} className="text-neutral-grey" />}
      </div>
      <RadixPopover.Root open={isOpen} onOpenChange={setOpen}>
        <RadixPopover.Anchor asChild>
          <div
            className={cn(
              "flex w-full items-center gap-8 rounded-medium border bg-neutral-white p-16 transition-colors focus-within:border-brand-blue",
              state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
              className,
            )}
          >
            <input
              id={inputId}
              value={value}
              placeholder={placeholder}
              autoComplete="off"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={isOpen ? `${listboxId}-${highlightedIndex}` : undefined}
              onChange={(event) => handleChange(event.target.value)}
              onFocus={() => setOpen(suggestions.length > 0)}
              onKeyDown={handleKeyDown}
              className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
            />
          </div>
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div id={listboxId} role="listbox">
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.placeId}
                  id={`${listboxId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => void handleSelect(suggestion)}
                  className={cn(
                    "w-full cursor-pointer rounded-small px-16 py-12 text-left text-16 text-neutral-black outline-none transition-colors duration-100",
                    index === highlightedIndex && "bg-tone-aqua-tint",
                  )}
                >
                  {suggestion.text}
                </button>
              ))}
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
